import api from "config/axiosConfig";
import { toast } from "sonner";
import type { CoauthorSuggestion } from "types/coauthor";
import { getPendingCoauthorRequests } from "./getObjectDetails";
import { Issue } from "util/constants";
import { getWikipediaSummary, parseWikipediaData, getWikiFullResolutionUrl } from "util/wikiUtils";
import { isPrerenderUserAgent } from "util/prerenderUserAgents";
import { apiRoutes } from "util/apiRoutes";

/**
 * Structured parts of an issue title that may represent an influence relationship.
 */
export interface IssueTitleParts {
  /** The influencer or the full title when not compound */
  influencer: string;
  /** The influenced/topic part when compound, otherwise null */
  influenced: string | null;
  /** True when the title represents a compound influence (contains '~') */
  isCompound: boolean;
}

/**
 * Splits an issue title into influencer and influenced parts using '~' as the separator.
 * If no '~' is present, returns the whole string as the influencer and influenced as null.
 *
 * Examples:
 *  - "A~B" -> { influencer: "A", influenced: "B", isCompound: true }
 *  - "A" -> { influencer: "A", influenced: null, isCompound: false }
 *
 * @param issueTitle - Raw issue title which may or may not include '~'
 * @returns IssueTitleParts containing influencer, influenced, and isCompound
 */
export const getIssueTitleParts = (issueTitle: string): IssueTitleParts => {
  // Runtime type safety in case this is called from non-TS code
  const safeTitle = typeof issueTitle === "string" ? issueTitle : String(issueTitle ?? "");
  const cleanTitle = safeTitle.trim();

  const tildeIndex = cleanTitle.indexOf("~");
  if (tildeIndex === -1) {
    return {
      influencer: cleanTitle,
      influenced: null,
      isCompound: false,
    };
  }

  const influencerRaw = cleanTitle.slice(0, tildeIndex).trim();
  const influencedRaw = cleanTitle.slice(tildeIndex + 1).trim();

  return {
    influencer: influencerRaw.length > 0 ? influencerRaw : cleanTitle,
    influenced: influencedRaw.length > 0 ? influencedRaw : null,
    isCompound: true,
  };
};

/**
 * Interface for entity with common properties needed for coauthor management
 */
export interface EntityWithCoauthors {
  id: number;
  user_id: number;
  properties?: {
    coauthors?: any[];
    [key: string]: any;
  };
}

/**
 * Interface for state setters used in coauthor management
 */
export interface CoauthorStateSetters {
  setExistingCoauthors: React.Dispatch<React.SetStateAction<CoauthorSuggestion[]>>;
  setNewCoauthors?: React.Dispatch<React.SetStateAction<CoauthorSuggestion[]>>;
  setRemovedCoauthors?: React.Dispatch<React.SetStateAction<CoauthorSuggestion[]>>;
  setPendingCoauthors: React.Dispatch<React.SetStateAction<CoauthorSuggestion[]>>;
  setIsLoadingCoauthors: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Resolve the backend API base URL with a safe fallback.
 * @returns Base URL string or empty string when unavailable.
 */
const getBackendBase = (): string => {
  const envBase = (process.env.REACT_APP_API_URL || "").trim();
  const fallbackBase = typeof window !== "undefined" ? window.location.origin : "";
  const base = envBase || fallbackBase;
  return base ? base.replace(/\/$/, "") : "";
};

/**
 * Determine whether a URL is a Wikipedia/Wikimedia image.
 * @param imageUrl - Absolute or relative image URL.
 * @returns True when the URL is hosted by Wikipedia/Wikimedia.
 */
const isWikipediaImageUrl = (imageUrl: string): boolean => {
  try {
    const parsed = new URL(imageUrl);
    const host = parsed.hostname.toLowerCase();
    return host === "upload.wikimedia.org"
      || host.endsWith(".wikipedia.org")
      || host.endsWith(".wikimedia.org");
  } catch (error) {
    return false;
  }
};

/**
 * Build a backend proxy URL for Wikipedia-hosted images.
 * @param imageUrl - Absolute image URL.
 * @returns Backend proxy URL string.
 */
const buildWikipediaImageProxyUrl = (imageUrl: string): string => {
  const base = getBackendBase();
  if (!base) {
    return imageUrl;
  }
  const url = new URL(`${base}${apiRoutes.utilities.wikipediaImageProxy}`);
  url.search = new URLSearchParams({ imageUrl }).toString();
  return url.toString();
};

/**
 * Conditionally proxy Wikipedia images for prerender user agents.
 * @param imageUrl - Absolute image URL.
 * @returns Proxied URL when needed, otherwise the original URL.
 */
const maybeProxyWikipediaImageUrl = (imageUrl?: string): string | undefined => {
  if (!imageUrl) {
    return undefined;
  }
  const trimmed = imageUrl.trim();
  if (!trimmed) {
    return undefined;
  }
  if (!isPrerenderUserAgent()) {
    return trimmed;
  }
  if (!isWikipediaImageUrl(trimmed)) {
    return trimmed;
  }
  return buildWikipediaImageProxyUrl(trimmed);
};

/**
 * Proxy Wikipedia-hosted image URLs for prerender user agents.
 * @param imageUrl - Candidate image URL (nullable).
 * @returns Proxied URL when needed, otherwise the original URL.
 */
export const getProxiedWikipediaImageUrl = (imageUrl?: string | null): string | undefined => {
  if (typeof imageUrl !== "string") {
    return undefined;
  }
  return maybeProxyWikipediaImageUrl(imageUrl) ?? imageUrl;
};

/**
 * Loads coauthor data for an entity, including existing and pending coauthors
 * 
 * @param entity - The entity to load coauthors for (impact, solution, etc.)
 * @param entityType - The type of entity (impact, solution, etc.)
 * @param currentUserId - The ID of the current user
 * @param stateSetters - Object containing state setter functions
 */
export const loadCoauthorData = async (
  entity: EntityWithCoauthors,
  entityType: string,
  currentUserId: number | undefined,
  stateSetters: CoauthorStateSetters
) => {
  const { setExistingCoauthors, setPendingCoauthors, setIsLoadingCoauthors } = stateSetters;

  setIsLoadingCoauthors(true);

  try {
    // Defense-in-depth: `entity.properties.coauthors` is the canonical list
    // of CONFIRMED collaborators after the 2026-04 coauthor split. Filter
    // any lingering `status: 'pending'` rows (legacy pre-migration data) so
    // public surfaces never reveal a pending invite that the invitee hasn't
    // accepted. Pending invites for the owner come from
    // `getPendingCoauthorRequests` below.
    const coauthorsArray = (entity.properties?.coauthors || []).filter(
      (coauthor: { status?: string } | null | undefined) =>
        coauthor !== null
        && coauthor !== undefined
        && coauthor.status !== "pending"
    );
    const coauthorPromises = coauthorsArray.map(async (coauthor: any) => {
      const type = coauthor.type || "user";
      const actorData = await (type === "user"
        ? api.get(apiRoutes.user.byId(coauthor.id))
        : api.get(apiRoutes.team.byId(coauthor.id)));

      return {
        id: coauthor.id,
        type,
        name: type === "user" ? actorData.data.username : actorData.data.teamname,
        display_name: actorData.data.display_name,
      } as CoauthorSuggestion;
    });

    const coauthorData = await Promise.all(coauthorPromises);
    setExistingCoauthors(coauthorData);

    // Only the owner loads outstanding invites; everyone else sees only
    // confirmed coauthors above.
    if (currentUserId && currentUserId === entity.user_id) {
      const pendingRequests = await getPendingCoauthorRequests(entityType, entity.id);
      setPendingCoauthors(pendingRequests);
    }
  } catch (error) {
    console.error("Error loading coauthor data:", error);
  } finally {
    setIsLoadingCoauthors(false);
  }
};

/**
 * Handles adding a new coauthor
 * 
 * @param suggestion - The coauthor suggestion to add
 * @param existingCoauthors - Array of existing coauthors
 * @param newCoauthors - Array of newly added coauthors
 * @param setNewCoauthors - State setter for newCoauthors
 * @param onCoauthorsChange - Optional callback when coauthors change
 * @param removedCoauthors - Array of removed coauthors
 * @returns Boolean indicating if the coauthor was added
 */
export const handleCoauthorSelect = (
  suggestion: CoauthorSuggestion,
  existingCoauthors: CoauthorSuggestion[],
  newCoauthors: CoauthorSuggestion[],
  setNewCoauthors: React.Dispatch<React.SetStateAction<CoauthorSuggestion[]>>,
  onCoauthorsChange?: (added: CoauthorSuggestion[], removed: CoauthorSuggestion[]) => void,
  removedCoauthors: CoauthorSuggestion[] = []
): boolean => {
  const isExisting = existingCoauthors.some(
    c => parseInt(c.id as any) === parseInt(suggestion.id as any) && c.type === suggestion.type
  );
  
  const isNew = newCoauthors.some(
    c => parseInt(c.id as any) === parseInt(suggestion.id as any) && c.type === suggestion.type
  );

  if (!isExisting && !isNew) {
    const updatedNewCoauthors = [...newCoauthors, suggestion];
    setNewCoauthors(updatedNewCoauthors);
    onCoauthorsChange?.(updatedNewCoauthors, removedCoauthors);
    return true;
  }
  
  return false;
};

/**
 * Handles removing a coauthor or cancelling a pending coauthor request
 * 
 * @param coauthor - The coauthor to remove
 * @param stateSetters - Object containing state setter functions
 * @param stateArrays - Object containing state arrays
 * @param onCoauthorsChange - Optional callback when coauthors change
 */
export const handleCoauthorRemove = async (
  coauthor: CoauthorSuggestion,
  stateSetters: Pick<CoauthorStateSetters, "setNewCoauthors" | "setRemovedCoauthors" | "setPendingCoauthors" | "setExistingCoauthors">,
  stateArrays: {
    newCoauthors: CoauthorSuggestion[];
    removedCoauthors: CoauthorSuggestion[];
  },
  onCoauthorsChange?: (added: CoauthorSuggestion[], removed: CoauthorSuggestion[]) => void
) => {
  const { setNewCoauthors, setRemovedCoauthors, setPendingCoauthors, setExistingCoauthors } = stateSetters;
  const { newCoauthors, removedCoauthors } = stateArrays;
  
  // If it's a pending coauthor, cancel the request.
  if (coauthor.isPending && coauthor.request_id) {
    try {
      await api.patch(apiRoutes.requests.byId(coauthor.request_id), { status: "cancelled" });
      setPendingCoauthors(prev =>
        prev.filter(pending => pending.request_id !== coauthor.request_id)
      );
      toast.success(`Invitation to ${coauthor.name} was cancelled`);
    } catch (error) {
      console.error("Error cancelling coauthor invitation:", error);
      toast.error("Failed to cancel invitation");
    }
    return;
  }
  
  // If the coauthor is new, remove it from newCoauthors and ensure it is not added to removedCoauthors.
  if (newCoauthors.some(c => parseInt(c.id as any) === parseInt(coauthor.id as any) && c.type === coauthor.type)) {
    const updatedNewCoauthors = newCoauthors.filter(
      c => !(parseInt(c.id as any) === parseInt(coauthor.id as any) && c.type === coauthor.type)
    );
    setNewCoauthors?.(updatedNewCoauthors);
    // Also, clear it from removedCoauthors if it exists.
    const updatedRemovedCoauthors = removedCoauthors.filter(
      c => !(parseInt(c.id as any) === parseInt(coauthor.id as any) && c.type === coauthor.type)
    );
    setRemovedCoauthors?.(updatedRemovedCoauthors);
    if (onCoauthorsChange) {
      onCoauthorsChange(updatedNewCoauthors, updatedRemovedCoauthors);
    }
  } else {
    // Remove from existing coauthors and add to removedCoauthors if not already present.
    setExistingCoauthors(prev =>
      prev.filter(c => !(parseInt(c.id as any) === parseInt(coauthor.id as any) && c.type === coauthor.type))
    );
    if (!removedCoauthors.some(c => parseInt(c.id as any) === parseInt(coauthor.id as any) && c.type === coauthor.type)) {
      const updatedRemovedCoauthors = [...removedCoauthors, coauthor];
      setRemovedCoauthors?.(updatedRemovedCoauthors);
      if (onCoauthorsChange) {
        onCoauthorsChange(newCoauthors, updatedRemovedCoauthors);
      }
    } else {
      if (onCoauthorsChange) {
        onCoauthorsChange(newCoauthors, removedCoauthors);
      }
    }
  }
};

/**
 * Returns the best thumbnail URL for an Issue, considering subject or topic context.
 * The resolution order prefers Wikipedia summary image (if provided), then CDN-backed
 * reference image keys, then parsed/thumbnail images on the entity, finally a default logo.
 *
 * @param issue - The Issue entity to inspect
 * @param options - Selection options
 * @param options.for - Whether to resolve for the subject or the topic side
 * @param options.cdnBase - Optional CDN base URL used to prefix reference image keys
 * @param options.wikipediaImageUrl - Optional Wikipedia summary image URL to prefer first
 * @returns A URL string for the thumbnail (never empty)
 */
export const getIssueThumbnailUrlFromEntity = (
  issue: Issue,
  options: { for: "subject" | "topic"; cdnBase?: string; wikipediaImageUrl?: string }
): string => {
  const DEFAULT_WIKI_LOGO = "https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg";
  const effectiveCdnBase = (options && options.cdnBase && options.cdnBase.length > 0)
    ? options.cdnBase
    : (process.env.REACT_APP_IMAGE_CDN || undefined);
  const wikipediaImageUrl = options?.wikipediaImageUrl;

  const prefixCdnIfNeeded = (maybeKeyOrUrl: string | undefined, cdnBase?: string): string | undefined => {
    if (!maybeKeyOrUrl) {
      return undefined;
    }
    const candidate = String(maybeKeyOrUrl).trim();
    if (candidate.length === 0) {
      return undefined;
    }
    if (/^https?:\/\//i.test(candidate)) {
      return candidate;
    }
    if (cdnBase && cdnBase.length > 0) {
      return `${cdnBase}/${candidate}`;
    }
    return candidate;
  };

  const fromSubject = (): string | undefined => {
    const wiki = wikipediaImageUrl && wikipediaImageUrl.trim().length > 0
      ? wikipediaImageUrl.trim()
      : undefined;
    if (wiki) {
      return wiki;
    }

    const refKeyUrl = prefixCdnIfNeeded(issue.properties?.reference_link_img_key as unknown as string | undefined, effectiveCdnBase);
    if (refKeyUrl) {
      return refKeyUrl;
    }

    const thumb = issue.properties?.subject_properties?.thumbnail?.source;
    if (thumb && String(thumb).trim().length > 0) {
      return String(thumb).trim();
    }

    const firstImg = issue.properties?.subject_properties?.wiki_parse_first_img;
    if (firstImg && String(firstImg).trim().length > 0) {
      return String(firstImg).trim();
    }

    return undefined;
  };

  const fromTopic = (): string | undefined => {
    const thumb = issue.properties?.topic_properties?.thumbnail?.source;
    if (thumb && String(thumb).trim().length > 0) {
      return String(thumb).trim();
    }
    const firstImg = issue.properties?.topic_properties?.wiki_parse_first_img;
    if (firstImg && String(firstImg).trim().length > 0) {
      return String(firstImg).trim();
    }
    return undefined;
  };

  const resolved = options.for === "subject" ? fromSubject() : fromTopic();
  const baseUrl = resolved ?? DEFAULT_WIKI_LOGO;
  return maybeProxyWikipediaImageUrl(baseUrl) ?? baseUrl;
};

/**
 * Fetches an Issue thumbnail URL using only the issue/article name.
 * Strategy: Wikipedia Summary.thumbnail → Wikipedia parsed first image → default logo
 * Returns a fully-qualified HTTPS URL.
 */
export const getIssueThumbnailUrl = async (issueTitle: string): Promise<string> => {
  const DEFAULT_WIKI_LOGO = "https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg";
  try {
    const summary = await getWikipediaSummary(issueTitle);
    const thumbnailFromSummary = summary?.thumbnail as string | null;
    if (thumbnailFromSummary && thumbnailFromSummary.length > 0) {
      return maybeProxyWikipediaImageUrl(thumbnailFromSummary) ?? thumbnailFromSummary;
    }

    const canonicalTitle = summary?.canonicalTitle || issueTitle;
    const parsed = await parseWikipediaData(canonicalTitle, true);
    const firstImg = parsed?.wiki_parse_first_img as string | null;
    if (firstImg && firstImg.length > 0) {
      const fullRes = getWikiFullResolutionUrl(firstImg);
      const resolved = fullRes || firstImg;
      return maybeProxyWikipediaImageUrl(resolved) ?? resolved;
    }

    return maybeProxyWikipediaImageUrl(DEFAULT_WIKI_LOGO) ?? DEFAULT_WIKI_LOGO;
  } catch (error) {
    console.error("Error resolving issue thumbnail by name:", error);
    return maybeProxyWikipediaImageUrl(DEFAULT_WIKI_LOGO) ?? DEFAULT_WIKI_LOGO;
  }
};
